import React from 'react';
import { motion } from 'framer-motion';
import { PhoneMockup } from './PhoneMockup';
import { FlameIcon, CheckCircleIcon, BarChart3Icon, LibraryIcon } from 'lucide-react';
export function AppPreviewSection() {
  return <section className="py-32 bg-ascension-black relative overflow-hidden" data-id="element-10">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-ascension-green/5 rounded-full blur-[150px] pointer-events-none" data-id="element-11" />

      <div className="max-w-7xl mx-auto px-6 mb-20 text-center relative z-10" data-id="element-12">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-13">
          Designed for Focus
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-ascension-gray max-w-2xl mx-auto" data-id="element-14">
          A minimal interface that gets out of your way.
        </motion.p>
      </div>

      <div className="relative h-[600px] max-w-7xl mx-auto flex justify-center items-center perspective-[2000px]" data-id="element-15">
        {/* Phone 1: Habit Library (Left, Back) */}
        <motion.div initial={{
        opacity: 0,
        x: -100,
        rotateY: 20,
        scale: 0.8
      }} whileInView={{
        opacity: 0.6,
        x: -250,
        rotateY: 15,
        scale: 0.85
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="absolute z-10 hidden lg:block" data-id="element-16">
          <PhoneMockup floating delay={0.5} data-id="element-17">
            <div className="flex flex-col h-full mt-8" data-id="element-18">
              <div className="flex items-center gap-2 mb-6" data-id="element-19">
                <LibraryIcon className="text-ascension-green" size={20} data-id="element-20" />
                <h3 className="text-white font-medium" data-id="element-21">Library</h3>
              </div>
              <div className="space-y-3" data-id="element-22">
                {[1, 2, 3, 4].map(i => <div key={i} className="bg-ascension-dark p-4 rounded-xl border border-white/5" data-id="element-23">
                    <div className="w-24 h-3 bg-white/20 rounded-full mb-2" data-id="element-24" />
                    <div className="w-16 h-2 bg-white/10 rounded-full" data-id="element-25" />
                  </div>)}
              </div>
            </div>
          </PhoneMockup>
        </motion.div>

        {/* Phone 2: Weekly Progress (Left, Mid) */}
        <motion.div initial={{
        opacity: 0,
        x: -50,
        rotateY: 10,
        scale: 0.9
      }} whileInView={{
        opacity: 0.8,
        x: -120,
        rotateY: 5,
        scale: 0.9
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.1,
        ease: 'easeOut'
      }} className="absolute z-20 hidden md:block" data-id="element-26">
          <PhoneMockup floating delay={0.2} data-id="element-27">
            <div className="flex flex-col h-full mt-8 items-center" data-id="element-28">
              <div className="flex items-center gap-2 mb-12 self-start" data-id="element-29">
                <BarChart3Icon className="text-ascension-green" size={20} data-id="element-30" />
                <h3 className="text-white font-medium" data-id="element-31">Progress</h3>
              </div>
              <div className="w-32 h-32 rounded-full border-8 border-ascension-dark flex items-center justify-center mb-8" data-id="element-32">
                <span className="text-2xl font-bold text-white" data-id="element-33">82%</span>
              </div>
              <div className="w-full space-y-4" data-id="element-34">
                <div className="flex justify-between text-xs text-ascension-gray" data-id="element-35">
                  <span data-id="element-36">Mon</span>
                  <span data-id="element-37">Tue</span>
                  <span data-id="element-38">Wed</span>
                  <span data-id="element-39">Thu</span>
                  <span data-id="element-40">Fri</span>
                </div>
                <div className="flex justify-between" data-id="element-41">
                  {[1, 2, 3, 4, 5].map(i => <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center ${i < 4 ? 'bg-ascension-green/20 text-ascension-green' : 'bg-ascension-dark text-white/30'}`} data-id="element-42">
                      {i < 4 ? <CheckCircleIcon size={14} data-id="element-43" /> : '-'}
                    </div>)}
                </div>
              </div>
            </div>
          </PhoneMockup>
        </motion.div>

        {/* Phone 3: Main Dashboard (Center, Front) */}
        <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: 'easeOut'
      }} className="absolute z-30" data-id="element-44">
          <PhoneMockup glowColor="green" floating delay={0} data-id="element-45">
            <div className="flex flex-col h-full mt-8" data-id="element-46">
              <div className="flex justify-center mb-8" data-id="element-47">
                <div className="bg-ascension-dark px-4 py-2 rounded-full border border-white/10 flex items-center gap-2" data-id="element-48">
                  <FlameIcon className="text-ascension-green fill-ascension-green" size={16} data-id="element-49" />
                  <span className="text-white font-medium text-sm" data-id="element-50">
                    21 Day Streak
                  </span>
                </div>
              </div>

              <div className="bg-ascension-dark rounded-2xl p-5 border border-ascension-green/30 shadow-[0_0_20px_rgba(68,198,87,0.1)]" data-id="element-51">
                <h4 className="text-white font-medium mb-1" data-id="element-52">
                  Morning Protocol
                </h4>
                <p className="text-xs text-ascension-gray mb-6" data-id="element-53">
                  Read 10 pages, Meditate 10m
                </p>

                <button className="w-full bg-ascension-green text-black py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2" data-id="element-54">
                  <CheckCircleIcon size={16} data-id="element-55" /> Mark Complete
                </button>
              </div>

              <div className="mt-4 bg-ascension-dark/50 rounded-2xl p-5 border border-white/5 opacity-50" data-id="element-56">
                <h4 className="text-white font-medium mb-1" data-id="element-57">Evening Review</h4>
                <p className="text-xs text-ascension-gray" data-id="element-58">Plan tomorrow</p>
              </div>
            </div>
          </PhoneMockup>
        </motion.div>

        {/* Phone 4: Task Confirmation (Right, Mid) */}
        <motion.div initial={{
        opacity: 0,
        x: 50,
        rotateY: -10,
        scale: 0.9
      }} whileInView={{
        opacity: 0.8,
        x: 120,
        rotateY: -5,
        scale: 0.9
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.3,
        ease: 'easeOut'
      }} className="absolute z-20 hidden md:block" data-id="element-59">
          <PhoneMockup floating delay={0.7} data-id="element-60">
            <div className="flex flex-col h-full items-center justify-center text-center px-4" data-id="element-61">
              <div className="w-16 h-16 rounded-full bg-ascension-green/20 flex items-center justify-center mb-6" data-id="element-62">
                <CheckCircleIcon className="text-ascension-green" size={32} data-id="element-63" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-64">
                Task Verified
              </h3>
              <p className="text-sm text-ascension-gray mb-8" data-id="element-65">
                Your apps have been unlocked for the next 4 hours.
              </p>

              <div className="w-full bg-ascension-dark p-4 rounded-xl border border-white/5" data-id="element-66">
                <span className="text-xs text-ascension-gray uppercase tracking-wider" data-id="element-67">
                  Streak Extended
                </span>
                <div className="text-2xl font-bold text-white mt-1" data-id="element-68">
                  22 Days
                </div>
              </div>
            </div>
          </PhoneMockup>
        </motion.div>
      </div>
    </section>;
}